import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, LogOut, User, LayoutDashboard, History, Languages, Shield, Menu, X, Sun, Moon, Trophy, Brain } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProfileSwitchButton } from "@/components/ProfileSwitchButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { profile, loading } = useProfile();
  const { language, changeLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { trackNavigation, trackClick } = useAnalytics();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Função para verificar se uma rota está ativa
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Função para verificar se está em um simulado (não mostrar BETA)
  const isInExam = () => {
    return location.pathname.includes('/exam/') || location.pathname.includes('/simulado/');
  };

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Error signing out:', error);
      } else {
        // Force navigation to home page after successful logout
        navigate("/", { replace: true });
        // window.location.reload(); // Removido para evitar problemas com F5
      }
    } catch (error) {
      console.error('Unexpected error during sign out:', error);
    }
  };

  return (
    <header className="border-b bg-background/80 backdrop-blur-md sticky top-0 z-50 relative shadow-sm">
      <div className="container-responsive">
        <div className="flex items-center justify-between py-3 sm:py-4 lg:py-5">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1 sm:gap-2 hover:opacity-80 transition-opacity">
            <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-primary flex-shrink-0" />
            <div className="hidden xs:block min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-sm sm:text-lg lg:text-xl font-bold truncate">{t('certify_lab')}</h1>
                {!isInExam() && (
                  <Badge variant="secondary" className="text-xs px-2 py-1 bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800">
                    BETA
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground hidden sm:block truncate">{t('cloud_native_simulations')}</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-4">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="hover:bg-primary/10 transition-colors"
              title={theme === 'light' ? 'Ativar modo escuro' : 'Ativar modo claro'}
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Languages className="h-4 w-4 mr-2" />
                  {language.toUpperCase()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48 bg-background border border-border shadow-lg z-[9999]">
                <DropdownMenuItem 
                  onClick={() => {
                    trackNavigation.languageChange(language, 'pt');
                    changeLanguage('pt');
                  }} 
                  className="cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  🇧🇷 Português {language === 'pt' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    trackNavigation.languageChange(language, 'en');
                    changeLanguage('en');
                  }} 
                  className="cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  🇺🇸 English {language === 'en' && '✓'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/dashboard">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={cn(
                      "relative transition-all duration-200 text-muted-foreground hover:text-foreground",
                      isActiveRoute('/dashboard') 
                        ? "text-primary border-b-2 border-secondary bg-blue-50 dark:bg-surface-1 dark:text-primary" 
                        : "hover:bg-surface-1 hover:text-primary dark:hover:text-primary"
                    )}
                  >
                    <LayoutDashboard className={cn(
                      "h-4 w-4 mr-2",
                      isActiveRoute('/dashboard') ? "text-primary" : ""
                    )} />
                    {t('dashboard')}
                  </Button>
                </Link>
                <Link to="/exams">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={cn(
                      "relative transition-all duration-200 text-muted-foreground hover:text-foreground",
                      isActiveRoute('/exams') 
                        ? "text-primary border-b-2 border-primary bg-blue-50 dark:bg-surface-1 dark:text-primary" 
                        : "hover:bg-surface-1 hover:text-primary dark:hover:text-primary"
                    )}
                  >
                    <BookOpen className={cn(
                      "h-4 w-4 mr-2",
                      isActiveRoute('/exams') ? "text-primary" : ""
                    )} />
                    {t('simulations')}
                  </Button>
                </Link>
                <Link to="/history">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={cn(
                      "relative transition-all duration-200 text-muted-foreground hover:text-foreground",
                      isActiveRoute('/history') 
                        ? "text-accent border-b-2 border-accent bg-pink-50 dark:bg-surface-1 dark:text-accent" 
                        : "hover:bg-surface-1 hover:text-accent dark:hover:text-accent"
                    )}
                  >
                    <History className={cn(
                      "h-4 w-4 mr-2",
                      isActiveRoute('/history') ? "text-accent" : ""
                    )} />
                    {t('history')}
                  </Button>
                </Link>
                <Link to="/learning">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={cn(
                      "relative transition-all duration-200 text-muted-foreground hover:text-foreground",
                      isActiveRoute('/learning') 
                        ? "text-blue-600 border-b-2 border-blue-500 bg-blue-50 dark:bg-surface-1 dark:text-blue-500" 
                        : "hover:bg-surface-1 hover:text-blue-600 dark:hover:text-blue-500"
                    )}
                  >
                    <Brain className={cn(
                      "h-4 w-4 mr-2",
                      isActiveRoute('/learning') ? "text-blue-600" : ""
                    )} />
                    Aprender
                  </Button>
                </Link>
                <Link to="/gamification">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className={cn(
                      "relative transition-all duration-200 text-muted-foreground hover:text-foreground",
                      isActiveRoute('/gamification') 
                        ? "text-yellow-600 border-b-2 border-yellow-500 bg-yellow-50 dark:bg-surface-1 dark:text-yellow-500" 
                        : "hover:bg-surface-1 hover:text-yellow-600 dark:hover:text-yellow-500"
                    )}
                  >
                    <Trophy className={cn(
                      "h-4 w-4 mr-2",
                      isActiveRoute('/gamification') ? "text-yellow-600" : ""
                    )} />
                    Gamificação
                  </Button>
                </Link>
                {profile?.role === 'admin' && (
                  <Link to="/admin">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className={cn(
                        "relative transition-all duration-200 text-muted-foreground hover:text-foreground",
                        isActiveRoute('/admin') 
                          ? "text-secondary border-b-2 border-secondary bg-pink-50 dark:bg-surface-1 dark:text-secondary" 
                          : "hover:bg-surface-1 hover:text-secondary dark:hover:text-secondary"
                      )}
                    >
                      <Shield className={cn(
                        "h-4 w-4 mr-2",
                        isActiveRoute('/admin') ? "text-secondary" : ""
                      )} />
                      Admin
                    </Button>
                  </Link>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span className="hidden xl:inline">{user.email?.split('@')[0]}</span>
                      {loading ? (
                        <Skeleton className="h-4 w-12" />
                      ) : profile ? (
                        <Badge variant="outline" className="text-xs">
                          {profile.role === 'admin' ? 'Admin' : 'Aluno'}
                        </Badge>
                      ) : null}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 z-[9999] bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden">
                    {/* Header com informações do usuário */}
                    <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {user.email?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-800 truncate">
                            {user.email?.split('@')[0]}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {user.email}
                          </div>
                        </div>
                      </div>
                      {loading ? (
                        <Skeleton className="h-4 w-16 mt-2" />
                      ) : profile ? (
                        <div className="mt-2 space-y-2">
                          <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                            {profile.role === 'admin' ? 'Administrador' : 'Aluno'}
                          </Badge>
                          <ProfileSwitchButton />
                        </div>
                      ) : null}
                    </div>
                    
                    {/* Menu items */}
                    <div className="py-2">
                      <DropdownMenuItem asChild className="min-h-[48px] hover:bg-blue-50 focus:bg-blue-50 transition-colors">
                        <Link to="/dashboard" className="flex items-center w-full px-4 py-3">
                          <LayoutDashboard className="h-5 w-5 mr-3 text-blue-600" />
                          <span className="font-medium text-slate-700">{t('dashboard')}</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="min-h-[48px] hover:bg-green-50 focus:bg-green-50 transition-colors">
                        <Link to="/exams" className="flex items-center w-full px-4 py-3">
                          <BookOpen className="h-5 w-5 mr-3 text-green-600" />
                          <span className="font-medium text-slate-700">{t('simulations')}</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="min-h-[48px] hover:bg-purple-50 focus:bg-purple-50 transition-colors">
                        <Link to="/history" className="flex items-center w-full px-4 py-3">
                          <History className="h-5 w-5 mr-3 text-purple-600" />
                          <span className="font-medium text-slate-700">{t('history')}</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="min-h-[48px] hover:bg-yellow-50 focus:bg-yellow-50 transition-colors">
                        <Link to="/gamification" className="flex items-center w-full px-4 py-3">
                          <Trophy className="h-5 w-5 mr-3 text-yellow-600" />
                          <span className="font-medium text-slate-700">Gamificação</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="min-h-[48px] hover:bg-indigo-50 focus:bg-indigo-50 transition-colors">
                        <Link to="/profile" className="flex items-center w-full px-4 py-3">
                          <User className="h-5 w-5 mr-3 text-indigo-600" />
                          <span className="font-medium text-slate-700">Meu Perfil</span>
                        </Link>
                      </DropdownMenuItem>
                      {profile?.role === 'admin' && (
                        <DropdownMenuItem asChild className="min-h-[48px] hover:bg-orange-50 focus:bg-orange-50 transition-colors">
                          <Link to="/admin" className="flex items-center w-full px-4 py-3">
                            <Shield className="h-5 w-5 mr-3 text-orange-600" />
                            <span className="font-medium text-slate-700">Administração</span>
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSignOut} className="min-h-[48px] text-red-600 focus:text-red-600 hover:bg-red-50 focus:bg-red-50 hover:text-red-700 focus:text-red-700 transition-colors px-4 py-3">
                        <LogOut className="h-5 w-5 mr-3" />
                        <span className="font-medium text-slate-700">{t('sign_out')}</span>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/auth">
                  <Button variant="ghost" size="sm">{t('sign_in')}</Button>
                </Link>
                <Link to="/auth">
                  <Button variant="primary" size="sm">{t('sign_up')}</Button>
                </Link>
              </div>
            )}
          </nav>

          {/* Mobile Navigation */}
          <div className="flex items-center gap-1 sm:gap-2 lg:hidden">
            {/* Theme Toggle Mobile */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="px-2 sm:px-3 min-h-[36px] min-w-[36px] hover:bg-primary/10 transition-colors"
              title={theme === 'light' ? 'Ativar modo escuro' : 'Ativar modo claro'}
            >
              {theme === 'light' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
              <span className="sr-only">{theme === 'light' ? 'Ativar modo escuro' : 'Ativar modo claro'}</span>
            </Button>

            {/* Language Switcher Mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="px-2 sm:px-3 min-h-[36px] min-w-[36px]">
                  <Languages className="h-4 w-4" />
                  <span className="sr-only">Alterar idioma</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-background border border-border shadow-lg z-[9999]">
                <DropdownMenuItem 
                  onClick={() => {
                    trackNavigation.languageChange(language, 'pt');
                    changeLanguage('pt');
                  }} 
                  className="min-h-[44px] cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  🇧🇷 Português {language === 'pt' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    trackNavigation.languageChange(language, 'en');
                    changeLanguage('en');
                  }} 
                  className="min-h-[44px] cursor-pointer hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                >
                  🇺🇸 English {language === 'en' && '✓'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="px-2 sm:px-3 min-h-[36px] min-w-[36px]">
                    <User className="h-4 w-4" />
                    <span className="sr-only">Menu do usuário</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 z-[9999] bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden">
                  {/* Header com informações do usuário */}
                  <div className="px-4 py-4 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {user.email?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-semibold text-slate-800 truncate">
                          {user.email?.split('@')[0]}
                        </div>
                        <div className="text-sm text-slate-500 truncate">
                          {user.email}
                        </div>
                      </div>
                    </div>
                    {loading ? (
                      <Skeleton className="h-4 w-20 mt-3" />
                    ) : profile ? (
                      <div className="mt-3">
                        <Badge variant="outline" className="text-sm bg-blue-100 text-blue-700 border-blue-200 px-3 py-1">
                          {profile.role === 'admin' ? 'Administrador' : 'Aluno'}
                        </Badge>
                      </div>
                    ) : null}
                  </div>
                  
                  {/* Menu items */}
                  <div className="py-2">
                    <DropdownMenuItem asChild className="min-h-[48px] hover:bg-blue-50 focus:bg-blue-50 transition-colors">
                      <Link to="/dashboard" className="flex items-center w-full px-4 py-3">
                        <LayoutDashboard className="h-5 w-5 mr-3 text-blue-600" />
                        <span className="font-medium text-slate-700">{t('dashboard')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="min-h-[48px] hover:bg-green-50 focus:bg-green-50 transition-colors">
                      <Link to="/exams" className="flex items-center w-full px-4 py-3">
                        <BookOpen className="h-5 w-5 mr-3 text-green-600" />
                        <span className="font-medium text-slate-700">{t('simulations')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="min-h-[48px] hover:bg-purple-50 focus:bg-purple-50 transition-colors">
                      <Link to="/history" className="flex items-center w-full px-4 py-3">
                        <History className="h-5 w-5 mr-3 text-purple-600" />
                        <span className="font-medium text-slate-700">{t('history')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="min-h-[48px] hover:bg-blue-50 focus:bg-blue-50 transition-colors">
                      <Link to="/learning" className="flex items-center w-full px-4 py-3">
                        <Brain className="h-5 w-5 mr-3 text-blue-600" />
                        <span className="font-medium text-slate-700">Aprender</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="min-h-[48px] hover:bg-yellow-50 focus:bg-yellow-50 transition-colors">
                      <Link to="/gamification" className="flex items-center w-full px-4 py-3">
                        <Trophy className="h-5 w-5 mr-3 text-yellow-600" />
                        <span className="font-medium text-slate-700">Gamificação</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="min-h-[48px] hover:bg-indigo-50 focus:bg-indigo-50 transition-colors">
                      <Link to="/profile" className="flex items-center w-full px-4 py-3">
                        <User className="h-5 w-5 mr-3 text-indigo-600" />
                        <span className="font-medium text-slate-700">Meu Perfil</span>
                      </Link>
                    </DropdownMenuItem>
                    {profile?.role === 'admin' && (
                      <DropdownMenuItem asChild className="min-h-[48px] hover:bg-orange-50 focus:bg-orange-50 transition-colors">
                        <Link to="/admin" className="flex items-center w-full px-4 py-3">
                          <Shield className="h-5 w-5 mr-3 text-orange-600" />
                          <span className="font-medium text-slate-700">Administração</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <div className="border-t border-slate-100 my-2"></div>
                    <DropdownMenuItem onClick={handleSignOut} className="min-h-[48px] text-red-600 focus:text-red-600 hover:bg-red-50 focus:bg-red-50 hover:text-red-700 focus:text-red-700 transition-colors px-4 py-3">
                      <LogOut className="h-5 w-5 mr-3" />
                      <span className="font-medium">{t('sign_out')}</span>
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="px-3 text-xs min-h-[36px]">
                    {t('sign_in')}
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="primary" size="sm" className="px-3 text-xs min-h-[36px] btn-gradient">
                    {t('sign_up')}
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;